import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import socket from '../socket/socket';

// How many refresh-and-retry rounds before we stop. A genuinely logged-out
// user must not spin: the retry only helps a token that a refresh can fix.
const MAX_RETRIES = 3;

/**
 * Recovers a socket the server rejected for a stale token.
 *
 * The handshake used to accept an unverifiable token and connect the socket
 * with no user: it joined no user room, so every user-scoped push (runners
 * coming online, above all) silently never arrived. The server now rejects it
 * instead, and socket.io treats a middleware error as fatal — it does not
 * reconnect on its own. So this refreshes the token and reconnects once the
 * refresh lands.
 *
 * Shared by web and mobile: the token transport differs (cookie vs
 * AsyncStorage) but the recovery does not.
 */
export function useSocketAuthRecovery(client) {
  const $ = _c(3);
  const retries = useRef(0);
  let t0;
  let t1;
  if ($[0] !== client) {
    t0 = () => {
      let ignore = false;
      const onConnect = () => {
        retries.current = 0;
      };
      const onConnectError = error => {
        if (!/token|session|unauthor/i.test(error.message)) {
          return;
        }
        if (retries.current >= MAX_RETRIES) {
          return;
        }
        retries.current = retries.current + 1;
        client.auth.refresh.query().then(t2 => {
          const [refreshError] = t2;
          if (ignore || refreshError) {
            return;
          }
          socket.connect();
        });
      };
      socket.on("connect", onConnect);
      socket.on("connect_error", onConnectError);
      return () => {
        ignore = true;
        socket.off("connect", onConnect);
        socket.off("connect_error", onConnectError);
      };
    };
    t1 = [client];
    $[0] = client;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}